/**
 * The bound on the body read of one JSON call of the DP shell. The module also holds the
 * 30-second value the shell uses for that bound and for the wait for the headers. The sources are
 * ruling R-127 of `2026-09-25-layered-shell-surfaces` and review row architecture-6 of the
 * feature 2026-10-03-shell-fetch-stream-timeout.
 *
 * `createAuthFetch` in `@zion/sdk-react` bounds the wait for the headers of each attempt, and that
 * deadline stops at the headers. So a server that sends its headers and then stalls the body holds
 * the call open for the life of the document. Each JSON client the shell owns closes that gap with
 * one {@link BodyDeadline} per call. The clients:
 *
 * - `request()` in this package's `system-api.ts`;
 * - `createCoreApiRequest` and `createRootShellSettingsSource` in the shell's `services/`;
 * - `request()` and `oauthFlowRequest()` in the shell's `api/client.ts`;
 * - `request()` and `deleteIdempotent()` in the shell's `home/api/home-api.ts`;
 * - `fetchTrailerManifest` in the shell's `home/api/manifest.ts`.
 *
 * Every client calls {@link createBodyDeadline}, so `git grep -n createBodyDeadline -- cloud/dp/shell`
 * is the census when this list and the tree disagree.
 *
 * This module is the one home of the shape and the value. The shell imports it as
 * `@zion/shell-system-surfaces/body-deadline`, the same way it imports `./protocol`. It imports
 * nothing, so the loader bundle and the system surfaces bundle can both carry it.
 */

/**
 * The longest wait, in milliseconds, for the response headers of one network attempt of the
 * shell's own requests and of a system mount, and for the body of one JSON call of the shell
 * once its headers are in. 30 seconds, the value R-64 chose and R-127 kept.
 */
export const SHELL_ATTEMPT_TIMEOUT_MS = 30_000;

/**
 * The largest delay, in milliseconds, that `setTimeout` keeps: 2^31 - 1. A larger delay fires
 * after 1 ms, so {@link createBodyDeadline} refuses it.
 */
const MAX_TIMER_DELAY_MS = 2_147_483_647;

/**
 * The bound on the body read of one call.
 *
 * The caller makes one per call with {@link createBodyDeadline}. It joins
 * {@link BodyDeadline.signal} into the request's `init.signal`, awaits the response headers, and
 * then reads the body inside {@link BodyDeadline.read}.
 */
export interface BodyDeadline {
  /**
   * The signal of the call's own `AbortController`. Nothing aborts it before
   * {@link BodyDeadline.read} arms the timer, so every attempt and retry before the headers sees a
   * live signal (R-64, E36). The timer aborts it with a `TimeoutError` `DOMException`, and the
   * fetch the signal joined then errors the body.
   */
  readonly signal: AbortSignal;
  /**
   * Join the call's own signals with {@link BodyDeadline.signal}, for the request's `init.signal`.
   *
   * Every client that has a caller signal to keep uses this one join, so the clients cannot drift
   * apart on how a caller abort and the deadline combine.
   *
   * @param callerSignals The signals the call must also obey, such as the caller's `init.signal`.
   *   A `null` or `undefined` entry adds nothing. No entry gives a signal that follows the
   *   deadline alone.
   * @returns A new signal that aborts when the first of its sources aborts, with that source's
   *   reason: a caller signal with the caller's reason, or the timer with its `TimeoutError`. A
   *   caller signal that is already aborted gives a signal that is already aborted. It arms no
   *   timer and changes no source.
   */
  join(...callerSignals: ReadonlyArray<AbortSignal | null | undefined>): AbortSignal;
  /**
   * Arm the timer now, run `read`, and clear the timer once `read` settles.
   *
   * - `read` resolves before the timer fires: the value, and the signal stays live.
   * - The timer fires first: the controller aborts, and the call rejects with that `TimeoutError`,
   *   whatever error the aborted body read gave.
   * - `read` rejects before the timer fires: that error, unchanged. A caller signal that aborts
   *   the body gives its own reason here.
   *
   * @param read The body read, such as `() => res.json()`.
   */
  read<T>(read: () => Promise<T>): Promise<T>;
}

/**
 * Build a {@link BodyDeadline}.
 *
 * @param timeoutMs The longest wait for the body once the headers are in, in milliseconds. A
 *   finite number above 0 and at most 2147483647, the largest delay `setTimeout` keeps.
 *   {@link SHELL_ATTEMPT_TIMEOUT_MS} in production; a test shortens it.
 * @returns A deadline whose timer is not armed. It starts no timer, sends nothing and holds no
 *   state outside its own controller.
 * @throws `RangeError` when `timeoutMs` is not a finite number above 0 and at most 2147483647.
 */
export function createBodyDeadline(timeoutMs: number): BodyDeadline {
  if (!(Number.isFinite(timeoutMs) && timeoutMs > 0 && timeoutMs <= MAX_TIMER_DELAY_MS)) {
    throw new RangeError(`timeoutMs must be a finite number above 0 and at most ${MAX_TIMER_DELAY_MS}, got ${timeoutMs}`);
  }
  const controller = new AbortController();
  return {
    signal: controller.signal,
    join(...callerSignals: ReadonlyArray<AbortSignal | null | undefined>): AbortSignal {
      return AbortSignal.any([
        ...callerSignals.filter((signal): signal is AbortSignal => signal instanceof AbortSignal),
        controller.signal,
      ]);
    },
    async read<T>(read: () => Promise<T>): Promise<T> {
      const timer = setTimeout(() => {
        controller.abort(new DOMException(`no complete response body within ${timeoutMs} ms`, 'TimeoutError'));
      }, timeoutMs);
      try {
        return await read();
      } catch (err) {
        if (!controller.signal.aborted) throw err;
        throw controller.signal.reason;
      } finally {
        clearTimeout(timer);
      }
    },
  };
}
